import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { GATEWAY_OWNERSHIPS, gatewayEuiSchema, type CreateGatewayRequest } from "@/contracts";
import { z } from "zod";

import { createGatewayMutation, organizationOptionsQuery, siteOptionsQuery } from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { SelectField, TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";
import { ApiError } from "@/lib/api-error";
import { GATEWAY_OWNERSHIP_LABELS } from "./gateway-labels";

/**
 * The EUI is validated with the contract's own schema rather than a copy, so
 * a paste with colons in it is accepted here exactly as the API accepts it.
 * Getting this wrong in the form would reject a value the server would have
 * taken, which is the most annoying kind of validation bug.
 */
const gatewayFormFields = z.object({
  organizationId: z.string(),
  eui: gatewayEuiSchema,
  name: z.string().trim().min(2, "Enter a name of at least 2 characters.").max(120),
  siteId: z.string(),
  ownership: z.enum(GATEWAY_OWNERSHIPS),
  notes: z.string().trim().max(500),
});

type GatewayFormValues = z.infer<typeof gatewayFormFields>;

export function GatewayCreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const client = useApiClient();
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const organizations = useQuery({ ...organizationOptionsQuery(client), enabled: isSuperAdmin });

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<GatewayFormValues>({
    resolver: zodResolver(gatewayFormFields),
    defaultValues: {
      organizationId: "",
      eui: "",
      name: "",
      siteId: "",
      ownership: "CUSTOMER",
      notes: "",
    },
  });

  // `useWatch` rather than `watch()`: it yields the value itself, which the
  // React compiler can reason about, instead of a function it has to skip.
  const organizationId = useWatch({ control, name: "organizationId" });
  const ownership = useWatch({ control, name: "ownership" });
  const siteId = useWatch({ control, name: "siteId" });

  // Shared infrastructure belongs to no tenant, so it sits on no customer
  // site either. Offering the picker anyway would invite a request the API
  // rejects, and the explanation belongs next to the choice that caused it.
  const isShared = isSuperAdmin && !organizationId;

  const sites = useQuery({
    ...siteOptionsQuery(client, organizationId || undefined),
    enabled: !isShared,
  });

  const mutation = useMutation({
    ...createGatewayMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["gateways"] });
      // A gateway sits on a site, and the sites list counts them.
      await queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
  });

  function close() {
    reset();
    mutation.reset();
    onClose();
  }

  const onSubmit = handleSubmit(async (values) => {
    const body: CreateGatewayRequest = {
      eui: values.eui,
      name: values.name,
      provider: "THE_THINGS_STACK",
      ownership: values.ownership,
      ...(values.organizationId ? { organizationId: values.organizationId } : {}),
      ...(values.siteId && !isShared ? { siteId: values.siteId } : {}),
      ...(values.notes ? { notes: values.notes } : {}),
    };

    try {
      await mutation.mutateAsync(body);
      close();
    } catch (error) {
      if (error instanceof ApiError) {
        for (const [field, message] of Object.entries(error.fieldErrors())) {
          if (field in gatewayFormFields.shape) {
            setError(field as keyof GatewayFormValues, { message });
          }
        }
      }
    }
  });

  return (
    <FormDialog
      open={open}
      title={t("dialogAddGatewayTitle")}
      description={t("dialogAddGatewayDescription")}
      submitLabel={t("registerGateway")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={close}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        {isSuperAdmin ? (
          <SelectField
            label={tCommon("organization")}
            hint={t("gatewayOrgHint")}
            {...(errors.organizationId?.message === undefined
              ? {}
              : { error: errors.organizationId.message })}
            value={organizationId}
            onChange={(value) =>
              setValue("organizationId", value, { shouldValidate: true, shouldDirty: true })
            }
            data={[
              { value: "", label: tCommon("sharedInfrastructure") },
              ...(organizations.data?.data ?? []).map((organization) => ({
                value: organization.id,
                label: organization.name,
              })),
            ]}
          />
        ) : null}

        <TextField
          label={t("fieldGatewayEui")}
          required
          placeholder={t("placeholderGatewayEui")}
          hint={t("hintGatewayEui")}
          {...(errors.eui?.message === undefined ? {} : { error: errors.eui.message })}
          {...register("eui")}
        />

        <TextField
          label={tCommon("name")}
          required
          placeholder={t("placeholderGatewayName")}
          hint={t("hintGatewayName")}
          {...(errors.name?.message === undefined ? {} : { error: errors.name.message })}
          {...register("name")}
        />

        <SelectField
          label={tCommon("ownedBy")}
          required
          hint={t("hintGatewayOwnership")}
          {...(errors.ownership?.message === undefined ? {} : { error: errors.ownership.message })}
          value={ownership}
          onChange={(value) =>
            setValue("ownership", value as GatewayFormValues["ownership"], {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
          data={GATEWAY_OWNERSHIPS.map((value) => ({
            value,
            label: GATEWAY_OWNERSHIP_LABELS[value],
          }))}
        />

        {isShared ? (
          <Alert tone="info">{t("gatewaySharedInfo")}</Alert>
        ) : (
          <SelectField
            label={tCommon("site")}
            hint={t("hintGatewaySite")}
            {...(errors.siteId?.message === undefined ? {} : { error: errors.siteId.message })}
            value={siteId}
            onChange={(value) =>
              setValue("siteId", value, { shouldValidate: true, shouldDirty: true })
            }
            data={[
              { value: "", label: t("gatewaySiteNotRecorded") },
              ...(sites.data?.data ?? []).map((site) => ({
                value: site.id,
                label: site.name,
              })),
            ]}
          />
        )}

        <TextField
          label={t("fieldInstallationNotes")}
          hint={t("hintGatewayNotes")}
          {...(errors.notes?.message === undefined ? {} : { error: errors.notes.message })}
          {...register("notes")}
        />

        {ownership === "THIRD_PARTY" ? (
          <Alert tone="info">{t("gatewayThirdPartyInfo")}</Alert>
        ) : null}
      </div>
    </FormDialog>
  );
}
